import styled from 'styled-components';
import { media } from '../../../styles/theme';

const BRAND = '#F1AF59';
const PASS = '#16a34a';
const FAIL = '#dc2626';
const IDLE = '#9ca3af';

/**
 * `$flush` drops the bottom padding for the tabs that embed a real timetable page below —
 * those pages bring their own padded container, and stacking the two reads as a gap.
 */
export const Page = styled.div`
  padding: 24px;
  padding-bottom: ${props => (props.$flush ? 0 : '24px')};
  max-width: 1600px;
  margin: 0 auto;

  ${media.phone} {
    padding: 12px;
    padding-bottom: ${props => (props.$flush ? 0 : '12px')};
  }
`;

export const Head = styled.div`
  margin-bottom: 16px;
`;

export const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 4px 0;
`;

export const Lede = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
  max-width: 74ch;
`;

export const Tabs = styled.div`
  display: inline-flex;
  background: #f3f4f6;
  border-radius: 8px;
  padding: 3px;
  gap: 2px;
  margin-bottom: 20px;
  max-width: 100%;
  overflow-x: auto;
`;

export const Tab = styled.button`
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  background: ${props => (props.$active ? 'white' : 'transparent')};
  color: ${props => (props.$active ? '#1f2937' : '#6b7280')};
  font-size: 14px;
  font-weight: ${props => (props.$active ? 600 : 500)};
  white-space: nowrap;
  cursor: pointer;
  box-shadow: ${props => (props.$active ? '0 1px 2px rgba(0,0,0,0.08)' : 'none')};

  &:hover {
    color: #1f2937;
  }
`;

/**
 * The guide reads as a column of cards. Two columns on a wide screen, one on a phone —
 * which is also the width this page is most often opened at, since checking the timetable
 * on a phone is the thing being tested.
 */
export const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
  gap: 16px;
  align-items: start;

  ${media.phone} {
    grid-template-columns: 1fr;
  }
`;

export const Card = styled.section`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 18px 20px;
  grid-column: ${props => (props.$wide ? '1 / -1' : 'auto')};
`;

export const CardTitle = styled.h2`
  font-size: 15px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 4px 0;
`;

export const CardNote = styled.p`
  font-size: 13px;
  color: #6b7280;
  margin: 0 0 14px 0;
  line-height: 1.55;

  code {
    font-size: 12px;
    background: rgba(0, 0, 0, 0.05);
    border-radius: 4px;
    padding: 1px 5px;
  }
`;

export const DefList = styled.dl`
  margin: 0;
  display: grid;
  grid-template-columns: minmax(96px, max-content) 1fr;
  gap: 8px 16px;
  font-size: 13px;

  dt {
    font-weight: 600;
    color: #374151;
  }

  dd {
    margin: 0;
    color: #4b5563;
    line-height: 1.55;
  }

  ${media.phone} {
    grid-template-columns: 1fr;
    gap: 2px 0;

    dd {
      margin-bottom: 10px;
    }
  }
`;

export const Steps = styled.ol`
  margin: 0;
  padding-left: 20px;
  font-size: 13px;
  color: #4b5563;
  line-height: 1.6;

  li {
    margin-bottom: 10px;
  }

  li:last-child {
    margin-bottom: 0;
  }

  strong {
    color: #1f2937;
  }

  em {
    font-style: normal;
    color: #6b7280;
  }

  code {
    font-size: 12px;
    background: rgba(0, 0, 0, 0.05);
    border-radius: 4px;
    padding: 1px 5px;
  }
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th {
    text-align: left;
    font-weight: 600;
    color: #4b5563;
    padding: 8px 10px;
    border-bottom: 1px solid #e5e7eb;
    white-space: nowrap;
  }

  td {
    padding: 8px 10px;
    border-bottom: 1px solid #f3f4f6;
    color: #374151;
    vertical-align: top;
    line-height: 1.5;
  }

  code {
    font-size: 12px;
    background: #f3f4f6;
    border-radius: 4px;
    padding: 1px 5px;
  }
`;

export const Scroller = styled.div`
  overflow-x: auto;
`;

/* ---- Self-check ---- */

export const CheckRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 10px 0;
  border-bottom: 1px solid #f3f4f6;

  &:last-child {
    border-bottom: none;
  }
`;

export const CheckMark = styled.span`
  flex: 0 0 auto;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  margin-top: 1px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  color: white;
  background: ${props => {
    if (props.$state === 'pass') return PASS;
    if (props.$state === 'fail') return FAIL;
    return IDLE;
  }};
`;

export const CheckBody = styled.div`
  font-size: 13px;
  line-height: 1.5;

  strong {
    color: #1f2937;
    font-weight: 600;
  }

  p {
    margin: 2px 0 0;
    color: #6b7280;
  }
`;

export const Banner = styled.div`
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  line-height: 1.55;
  margin-bottom: 14px;
  background: ${props => (props.$tone === 'warn' ? '#fff7ed' : '#f0fdf4')};
  border: 1px solid ${props => (props.$tone === 'warn' ? '#fed7aa' : '#bbf7d0')};
  color: ${props => (props.$tone === 'warn' ? '#9a3412' : '#166534')};

  code {
    font-size: 12px;
    background: rgba(0, 0, 0, 0.05);
    border-radius: 4px;
    padding: 1px 5px;
  }
`;

export const Button = styled.button`
  padding: 8px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: white;
  color: #374151;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: ${BRAND};
    color: #1f2937;
  }

  &:disabled {
    opacity: 0.55;
    cursor: default;
  }
`;

export const Muted = styled.p`
  font-size: 12px;
  color: #9ca3af;
  margin: 12px 0 0;
  line-height: 1.55;

  code {
    font-size: 12px;
    background: rgba(0, 0, 0, 0.05);
    border-radius: 4px;
    padding: 1px 5px;
  }
`;
